/**
 * The Gantt toolbar (spec 5.2 / 6): free-text search (well / API / slot id), phase
 * filter, the future-locations-only toggle (default ON), the current window label, and
 * the truthful "N of M shown" readout. Filter state lives in the schedule-dialog atoms;
 * row-model derivations happen upstream. Per-unit expand is via the group chevrons.
 */

import { useAtom } from 'jotai';
import { Dropdown, Search, Toggle } from '@carbon/react';
import { FormattedCount } from 'components/formatted-count';
import { PHASE_LABELS, PHASE_ORDER } from './constants';
import { futureOnlyAtom, phaseFilterAtom, searchQueryAtom } from './atoms';
import type { PhaseKey } from './types';
import styles from './schedule-toolbar.module.scss';

interface PhaseOption {
  id: PhaseKey | 'all';
  label: string;
}

const PHASE_OPTIONS: PhaseOption[] = [
  { id: 'all', label: 'All phases' },
  ...PHASE_ORDER.map((phase) => ({ id: phase, label: PHASE_LABELS[phase] })),
];

export interface ScheduleToolbarProps {
  windowLabel: string;
  shownRowCount: number;
  totalRowCount: number;
}

/**
 * Renders the chart toolbar row.
 *
 * @param {ScheduleToolbarProps} props - The window label and the shown/total counts.
 * @return {JSX.Element} The toolbar.
 */
export function ScheduleToolbar({
  windowLabel,
  shownRowCount,
  totalRowCount,
}: ScheduleToolbarProps) {
  const [search, setSearch] = useAtom(searchQueryAtom);
  const [phaseFilter, setPhaseFilter] = useAtom(phaseFilterAtom);
  const [futureOnly, setFutureOnly] = useAtom(futureOnlyAtom);

  const selectedPhase =
    PHASE_OPTIONS.find((option) => option.id === (phaseFilter ?? 'all')) ??
    PHASE_OPTIONS[0];

  return (
    <div className={styles.toolbar}>
      <Search
        size="sm"
        className={styles.search}
        labelText="Search wells"
        placeholder="Search well, API, or slot id"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />

      <Dropdown<PhaseOption>
        id="schedule-phase-filter"
        className={styles.phase}
        size="sm"
        label="Phase"
        titleText="Phase"
        hideLabel
        items={PHASE_OPTIONS}
        selectedItem={selectedPhase}
        itemToString={(item) => item?.label ?? ''}
        onChange={({ selectedItem }) =>
          setPhaseFilter(
            !selectedItem || selectedItem.id === 'all'
              ? null
              : (selectedItem.id as PhaseKey)
          )
        }
      />

      <div className={styles.futureToggle}>
        <span
          id="schedule-future-only-label"
          className={styles.futureToggleLabel}
        >
          Future locations only
        </span>
        <Toggle
          id="schedule-future-only"
          size="sm"
          aria-labelledby="schedule-future-only-label"
          labelA="Off"
          labelB="On"
          toggled={futureOnly}
          onToggle={(checked) => setFutureOnly(checked)}
        />
      </div>

      <span className={styles.window}>{windowLabel}</span>
      <span className={styles.shown} aria-live="polite">
        <FormattedCount value={shownRowCount} /> of{' '}
        <FormattedCount value={totalRowCount} /> shown
      </span>
    </div>
  );
}
